import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import MobileGameRow from './MobileGameRow';
import GameDetailModal from '../GameDetailModal/GameDetailModal';
import MobileGenericLockCard from './MobileGenericLockCard';
import MobileWatchList from './MobileWatchList';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useFilterContext } from '../../context/FilterContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { applyFilters } from '../../utils/filterUtils';
import { formatWeekLabel } from '../../utils/weekLabel';
import styles from './MobileWeeklyPickSheet.module.css';
import MobileGameRowSkeletonList from '../loading/MobileGameRowSkeletonList';

const MobileWeeklyPickSheet = ({ displayedWeek, onWeekChange }) => {
  const {
    games,
    locksCache,
    watchedPicks,
    toggleWatchPick,
    requestDirectLock,
    user,
    getMaxPicksForWeek,
    loadedGamesWeek,
    isGamesLoading,
  } = useGameContext();

  const { currentWeek, calendar } = useCurrentWeek();
  const { filters, hasActiveFilters } = useFilterContext();
  const { canSeeLocks } = useLeagueSettings();
  const [selectedGame, setSelectedGame] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isWatchListOpen, setIsWatchListOpen] = useState(false);

  const sortedGames = useMemo(() => {
    // Apply filters to games
    const filteredGames = applyFilters(games, filters);
    return [...filteredGames].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }, [games, filters]);

  // Check if user has reached max picks for the current week
  const hasReachedMaxPicks = useCallback(() => {
    if (!displayedWeek || !locksCache[displayedWeek]) return false;

    const locksForWeek = locksCache[displayedWeek];
    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    const maxPicksForWeek = getMaxPicksForWeek(displayedWeek);

    return userLocksCount >= maxPicksForWeek;
  }, [displayedWeek, locksCache, user, getMaxPicksForWeek]);

  // Get games that have locks for generic cards
  const gamesWithLocks = useMemo(() => {
    if (!displayedWeek || !locksCache[displayedWeek]) return [];

    const lockedGameIds = new Set();
    locksCache[displayedWeek].forEach(lock => {
      lockedGameIds.add(lock.gameId);
    });

    // Only include games that are in the current filtered/sorted list
    return sortedGames.filter(game => lockedGameIds.has(game.id));
  }, [sortedGames, locksCache, displayedWeek]);

  // Get all current user's locks for this week
  const userLocksForWeek = useMemo(() => {
    if (!displayedWeek || !locksCache[displayedWeek] || !user) return [];

    const userLocks = locksCache[displayedWeek].filter(lock =>
      lock.userId === user.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    );

    if (userLocks.length === 0) return [];

    // Get all user locks with their corresponding games
    return userLocks
      .map(userLock => {
        const userLockGame = sortedGames.find(game => game.id === userLock.gameId);
        return userLockGame ? { game: userLockGame, lock: userLock } : null;
      })
      .filter(Boolean); // Remove any null entries where game wasn't found
  }, [displayedWeek, locksCache, user, sortedGames]);

  // Determine if we should show generic cards (States 2 & 4 pre-lock)
  const shouldShowGenericCards = useMemo(() => {
    // Don't show generic cards when filters are active - they would reveal which games have locks
    if (hasActiveFilters) {
      return false;
    }
    return !canSeeLocks() && !hasReachedMaxPicks() && gamesWithLocks.length > 0;
  }, [canSeeLocks, hasReachedMaxPicks, gamesWithLocks.length, hasActiveFilters]);

  const handleGameTap = useCallback((game) => {
    setSelectedGame(game);
    setIsDetailOpen(true);
  }, []);

  const handlePickAction = useCallback(async ({ gameId, pickType, lockLine, action }) => {
    try {
      // Ensure we have a valid week number
      const weekToUse = displayedWeek || currentWeek;
      console.log('Mobile pick action:', { gameId, pickType, lockLine, action, displayedWeek, currentWeek, weekToUse });

      if (!weekToUse) {
        console.error('No valid week number available for pick action');
        return;
      }

      if (action === 'watch') {
        const game = games.find((g) => g.id === gameId);
        const watchLine = lockLine;
        const storedLiveLine =
          pickType === 'over' || pickType === 'under'
            ? game?.betting?.over_under ?? lockLine
            : game?.betting?.spread ?? lockLine;
        toggleWatchPick(gameId, pickType, weekToUse, watchLine, storedLiveLine, false);
      } else if (action === 'lock') {
        const game = games.find((g) => g.id === gameId);
        const homeTeam = typeof game?.home_team === 'object' ? game.home_team.name : game?.home_team;
        const awayTeam = typeof game?.away_team === 'object' ? game.away_team.name : game?.away_team;
        requestDirectLock(
          {
            gameId,
            pickType,
            lockLine,
            liveLine:
              pickType === 'over' || pickType === 'under'
                ? game?.betting?.over_under
                : game?.betting?.spread,
            weekNumber: weekToUse,
            id: `${gameId}_${pickType}`,
            team:
              pickType === 'home'
                ? homeTeam
                : pickType === 'away'
                  ? awayTeam
                  : pickType.charAt(0).toUpperCase() + pickType.slice(1),
          },
          weekToUse
        );
      }
    } catch (error) {
      console.error('Error handling pick action:', error);
    }
  }, [toggleWatchPick, requestDirectLock, displayedWeek, currentWeek, games]);

  const watchCountForWeek = useMemo(() => {
    if (!displayedWeek) return 0;
    return watchedPicks.filter(
      (pick) => pick.weekNumber === displayedWeek && pick.pickType !== 'bye'
    ).length;
  }, [watchedPicks, displayedWeek]);

  const handleCloseDetail = useCallback(() => {
    setIsDetailOpen(false);
    setSelectedGame(null);
  }, []);

  const isWeekGamesReady = loadedGamesWeek === displayedWeek;
  const isWeekGamesLoading = isGamesLoading || !isWeekGamesReady;

  return (
    <div className={styles.container}>
      <div className={styles.gamesList}>
        {isWeekGamesLoading ? (
          <MobileGameRowSkeletonList count={5} />
        ) : sortedGames.length === 0 ? (
          <div className={styles.emptyState}>
            <p>
              There are no scheduled games available for{' '}
              {formatWeekLabel(displayedWeek, calendar)}. Check back later.
            </p>
          </div>
        ) : (
          <>
            {/* User's locks for this week */}
            {userLocksForWeek.length > 0 && (
              <div className={styles.userLockSection}>
                <h3 className={styles.userLockHeader}>
                  Your Week {displayedWeek} Lock{userLocksForWeek.length > 1 ? 's' : ''}
                </h3>
                {userLocksForWeek.map((userLockData, index) => (
                  <MobileGameRow
                    key={`user-lock-${userLockData.game.id}-${index}`}
                    game={userLockData.game}
                    displayedWeek={displayedWeek}
                    locksCache={locksCache}
                    onGameTap={handleGameTap}
                    shouldHideLockIndicators={false}
                    isUserLock={true}
                    userLock={userLockData.lock}
                  />
                ))}
              </div>
            )}

            {/* Generic lock cards for States 2 & 4 pre-lock */}
            {shouldShowGenericCards && (
              <div className={styles.genericCardsSection}>
                {gamesWithLocks.map(game => {
                  const gameLocks = locksCache[displayedWeek]?.filter(lock => lock.gameId === game.id) || [];
                  return (
                    <MobileGenericLockCard
                      key={`generic-${game.id}`}
                      game={game}
                      locks={gameLocks}
                      displayedWeek={displayedWeek}
                    />
                  );
                })}
              </div>
            )}

            {/* Regular game cards - exclude user's lock games if they exist */}
            {sortedGames
              .filter(game => !userLocksForWeek.some(userLockData => userLockData.game.id === game.id))
              .map(game => (
                <MobileGameRow
                  key={game.id}
                  game={game}
                  displayedWeek={displayedWeek}
                  locksCache={locksCache}
                  onGameTap={handleGameTap}
                  shouldHideLockIndicators={!canSeeLocks() && !hasReachedMaxPicks()}
                />
              ))}
          </>
        )}
      </div>

      {selectedGame && (
        <GameDetailModal
          game={selectedGame}
          displayedWeek={displayedWeek}
          isOpen={isDetailOpen}
          onClose={handleCloseDetail}
          onPickAction={handlePickAction}
          hasReachedMaxPicks={hasReachedMaxPicks()}
          initialSelectedPick={null}
        />
      )}

      {displayedWeek && (
        <>
          <button
            type="button"
            className={styles.watchListFab}
            onClick={() => setIsWatchListOpen(true)}
            aria-label="Open watch list"
          >
            Watches
            {watchCountForWeek > 0 && (
              <span className={styles.watchBadge}>{watchCountForWeek}</span>
            )}
          </button>
          <MobileWatchList
            week={displayedWeek}
            games={games}
            isOpen={isWatchListOpen}
            onClose={() => setIsWatchListOpen(false)}
          />
        </>
      )}
    </div>
  );
};

MobileWeeklyPickSheet.propTypes = {
  displayedWeek: PropTypes.number,
  onWeekChange: PropTypes.func.isRequired
};

export default MobileWeeklyPickSheet;
